import { createSelector } from '@reduxjs/toolkit';
import { Store } from 'components/common/types/Store.types';
import { PlannerSecondaryPanelType } from '../types';
import { CampaignVariant, getCampaignVariant } from './getCampaignVariant';
import {
  getIsSchedulePanelFilled,
  getIsEnvironmentPanelFilled,
  getIsMediaOwnerPanelFilled,
  getIsCpmPanelFilled,
  getIsMediaTypePanelFilled,
  getIsTargetsPanelFilled,
  getIsNetworksPanelFilled,
  getIsFrameCPMPanelFilled,
  getIsDSPPanelFilled,
} from './getIsPanelFilledPredicate';
import { getDirectSalesNetworkCriteria } from './getDirectSalesTargetsCriteria';

export type PanelEntry = {
  type: PlannerSecondaryPanelType;
  requiredFor: CampaignVariant[];
  getIsFilled?: (state: Store) => boolean;
  getIsEnabled?: (state: Store) => boolean;
  getIsConditionallyRequired?: (state: Store) => boolean;
};

const getIsTargetsMandatory = createSelector(
  [getCampaignVariant, getDirectSalesNetworkCriteria],
  (variant, networkCriteria): boolean => {
    if (variant !== 'DSM') return true;

    return networkCriteria.length === 0;
  },
);

const getIsNetworksMandatory = createSelector(
  [
    getCampaignVariant,
    (state: Store) => state.dealManagement.commonDeal.currentLine.budget,
    (state: Store) => state.dealManagement.commonDeal.currentLine.frames,
    (state: Store) => state.dealManagement.commonDeal.currentLine.impressions,
  ],
  (variant, budget, frames, impressions): boolean => {
    if (variant !== 'DSM') return true;

    return !budget && !frames && !impressions;
  },
);

export const REQUIRED_PANELS: PanelEntry[] = [
  {
    type: PlannerSecondaryPanelType.SCHEDULE,
    requiredFor: ['PG', 'NG', 'RESELLER', 'VIOOH_SELECT', 'DSM'],
    getIsFilled: getIsSchedulePanelFilled,
  },
  {
    type: PlannerSecondaryPanelType.CPM,
    requiredFor: ['PG', 'NG', 'RESELLER', 'VIOOH_SELECT'],
    getIsFilled: getIsCpmPanelFilled,
  },
  {
    type: PlannerSecondaryPanelType.DSP,
    requiredFor: ['PG', 'NG', 'RESELLER', 'VIOOH_SELECT'],
    getIsFilled: getIsDSPPanelFilled,
  },
  { type: PlannerSecondaryPanelType.CHANNEL, requiredFor: ['DSM'], getIsFilled: getIsEnvironmentPanelFilled },
  { type: PlannerSecondaryPanelType.ENVIRONMENT, requiredFor: ['PG', 'NG'], getIsFilled: getIsEnvironmentPanelFilled },
  {
    type: PlannerSecondaryPanelType.MEDIA_OWNER,
    requiredFor: ['RESELLER', 'VIOOH_SELECT'],
    getIsFilled: getIsMediaOwnerPanelFilled,
  },
  {
    type: PlannerSecondaryPanelType.FRAME_CPM,
    requiredFor: ['VIOOH_SELECT'],
    getIsFilled: getIsFrameCPMPanelFilled,
  },
  { type: PlannerSecondaryPanelType.MEDIA_TYPE, requiredFor: ['DSM'], getIsFilled: getIsMediaTypePanelFilled },
  {
    type: PlannerSecondaryPanelType.TARGETS,
    requiredFor: ['PG', 'DSM'],
    getIsFilled: getIsTargetsPanelFilled,
    getIsConditionallyRequired: getIsTargetsMandatory,
  },
  {
    type: PlannerSecondaryPanelType.NETWORKS,
    requiredFor: ['DSM'],
    getIsFilled: getIsNetworksPanelFilled,
    getIsConditionallyRequired: getIsNetworksMandatory,
  },
];

const allFilledPredicates = REQUIRED_PANELS.map((panel) => panel.getIsFilled).filter(
  (predicate): predicate is (state: Store) => boolean => Boolean(predicate),
);

export const getCurrentPanelFlow = createSelector(
  [getCampaignVariant, (state: Store) => state],
  (variant, state): PlannerSecondaryPanelType[] =>
    REQUIRED_PANELS.filter((panel) => panel.requiredFor.includes(variant) && (panel.getIsEnabled?.(state) ?? true)).map(
      (panel) => panel.type,
    ),
);

export const getFilledPanels = createSelector(
  [...allFilledPredicates],
  (...filledResults: boolean[]): Set<PlannerSecondaryPanelType> => {
    return REQUIRED_PANELS.reduce((filled, panel) => {
      if (!panel.getIsFilled) return filled;

      const indexOfFilledPredicate = allFilledPredicates.indexOf(panel.getIsFilled);
      if (filledResults[indexOfFilledPredicate]) filled.add(panel.type);

      return filled;
    }, new Set<PlannerSecondaryPanelType>());
  },
);

export const getIsCurrentPanelRequired = createSelector(
  getCurrentPanelFlow,
  (state: Store) => state.dealManagement.plannerSecondaryPanelType,
  (state: Store) => state,
  (currentPanelFlow, plannerSecondaryPanelType, state): boolean =>
    currentPanelFlow.includes(plannerSecondaryPanelType) &&
    (REQUIRED_PANELS.find((panel) => panel.type === plannerSecondaryPanelType)?.getIsConditionallyRequired?.(state) ??
      true),
);

export const getIsCurrentPanelFilled = createSelector(
  getFilledPanels,
  (state: Store) => state.dealManagement.plannerSecondaryPanelType,
  (filledPanels, plannerSecondaryPanelType): boolean => filledPanels.has(plannerSecondaryPanelType),
);

export const shouldSkipNextRequiredPanelException = (
  currentPanel: PlannerSecondaryPanelType,
  nextRequiredPanel: PlannerSecondaryPanelType,
): boolean => {
  return (
    (currentPanel === PlannerSecondaryPanelType.TARGETS && nextRequiredPanel === PlannerSecondaryPanelType.NETWORKS) ||
    (currentPanel === PlannerSecondaryPanelType.NETWORKS && nextRequiredPanel === PlannerSecondaryPanelType.TARGETS)
  );
};

export const getNextRequiredPanel = createSelector(
  getCurrentPanelFlow,
  getFilledPanels,
  (state: Store) => state.dealManagement.plannerSecondaryPanelType,
  (state: Store) => state,
  (currentPanelFlow, filledPanels, plannerSecondaryPanelType, state): PlannerSecondaryPanelType => {
    const indexOfCurrentPanel = currentPanelFlow.indexOf(plannerSecondaryPanelType);

    if (indexOfCurrentPanel === -1) return PlannerSecondaryPanelType.NONE;

    const searchOrder = [
      ...currentPanelFlow.slice(indexOfCurrentPanel + 1),
      ...currentPanelFlow.slice(0, indexOfCurrentPanel),
    ];

    return (
      searchOrder.find((panelType) => {
        const panel = REQUIRED_PANELS.find(({ type }) => type === panelType);

        return (
          (panel?.getIsEnabled?.(state) ?? true) &&
          (panel?.getIsConditionallyRequired?.(state) ?? true) &&
          !filledPanels.has(panelType)
        );
      }) ?? PlannerSecondaryPanelType.NONE
    );
  },
);
